<template>
  <div class="ma-page">
    <site-header />

    <section class="photo-page">
      <div class="section-heading">
        <span>Photothèque</span>
        <h1>Nos images, nos moments.</h1>
        <p>
          Les formations que nous donnons en classe — de nos salles jusqu'à Monaco.
        </p>
      </div>

      <section
        v-for="group in groups"
        :id="group.id"
        :key="group.id"
        class="photo-group"
      >
        <div class="group-heading">
          <span>{{ group.kicker }}</span>
          <h2>{{ group.title }}</h2>
          <p>{{ group.intro }}</p>
        </div>

        <div class="photo-grid">
          <article
            v-for="photo in group.photos"
            :key="photo.src"
            class="photo-card"
          >
            <button
              type="button"
              class="photo-frame"
              :aria-label="photo.alt"
              @click="open(photo)"
            >
              <img
                :src="photo.src"
                :alt="photo.alt"
                loading="lazy"
              >
            </button>
            <h3>{{ photo.title }}</h3>
            <p>{{ photo.caption }}</p>
          </article>
        </div>
      </section>
    </section>

    <footer class="site-footer">
      <div class="footer-main">
        <div class="footer-brand">
          <div class="footer-logo">
            <img
              src="/minds-academy-mark.png"
              alt="Minds' Academy"
            >
            <span>Minds' Academy</span>
          </div>
          <p>La plateforme qui réunit apprentissage, accompagnement et gestion pédagogique.</p>
          <router-link
            :to="{ name: 'contact' }"
            class="footer-contact"
          >
            Nous contacter
          </router-link>
        </div>
        <div>
          <h3>Plateforme</h3>
          <router-link :to="{ name: 'videos' }">
            Nos vidéos
          </router-link>
          <router-link :to="{ name: 'phototheque' }">
            Photothèque
          </router-link>
        </div>
        <div>
          <h3>Accès</h3>
          <router-link :to="{ name: 'login-student' }">
            Espace étudiant
          </router-link>
          <router-link :to="{ name: 'minds-register' }">
            Créer un compte
          </router-link>
        </div>
        <div>
          <h3>Légal</h3>
          <router-link :to="{ name: 'privacy-policy' }">
            Confidentialité
          </router-link>
          <router-link :to="{ name: 'terms-of-use' }">
            Conditions d'utilisation
          </router-link>
        </div>
      </div>
      <div class="footer-bottom">
        <span>© 2026 Minds' Academy. Tous droits réservés.</span>
        <span>www.mindsacademy.io</span>
      </div>
    </footer>

    <div
      v-if="active"
      class="lightbox"
      @click.self="active = null"
    >
      <button
        type="button"
        class="lightbox-close"
        aria-label="Fermer"
        @click="active = null"
      >
        ×
      </button>
      <img
        :src="active.src"
        :alt="active.alt"
      >
      <div class="lightbox-caption">
        <strong>{{ active.title }}</strong>
        <p>{{ active.caption }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { PHOTO_GROUPS } from '@/data/phototheque'
import SiteHeader from '@/views/home/HomePage2/components/SiteHeader.vue'

export default {
  name: 'PhotothequePage',
  components: { SiteHeader },
  data() {
    return {
      groups: PHOTO_GROUPS,
      active: null,
    }
  },
  mounted() {
    document.title = "Photothèque — Minds' Academy"
    window.scrollTo(0, 0)
  },
  methods: {
    open(photo) {
      this.active = photo
    },
  },
}
</script>

<style scoped>
.ma-page {
  --cream: #fffaf3;
  --ink: #100707;
  --teal: #18b9a7;
  --teal-dark: #0d756c;
  --coral: #ff6f59;
  min-height: 100vh;
  background: var(--cream);
  color: var(--ink);
  font-family: Inter, "Helvetica Neue", Arial, sans-serif;
}

.ma-page *,
.ma-page *::before,
.ma-page *::after {
  box-sizing: border-box;
}

.ma-page a {
  color: inherit;
  text-decoration: none;
}

.photo-page {
  padding: 70px clamp(24px, 8vw, 130px) 90px;
}

.section-heading {
  max-width: 720px;
  margin: 0 auto 56px;
  text-align: center;
}

.section-heading span,
.group-heading span {
  display: inline-block;
  margin-bottom: 12px;
  padding: 6px 12px;
  border: 1px solid var(--ink);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.section-heading h1 {
  margin: 0;
  font-size: clamp(42px, 5vw, 68px);
  letter-spacing: -0.055em;
  line-height: 0.98;
}

.section-heading p,
.group-heading p {
  margin: 16px 0 0;
  font-size: 17px;
  line-height: 1.6;
  opacity: 0.72;
}

.photo-group + .photo-group {
  margin-top: 72px;
}

.group-heading {
  max-width: 640px;
  margin-bottom: 28px;
}

.group-heading h2 {
  margin: 0;
  font-size: clamp(28px, 3vw, 38px);
  letter-spacing: -0.04em;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
}

.photo-card h3 {
  margin: 14px 0 0;
  font-size: 18px;
  letter-spacing: -0.02em;
}

.photo-card p {
  margin: 8px 0 0;
  font-size: 15px;
  line-height: 1.55;
  opacity: 0.72;
}

.photo-frame {
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 16px;
  background: #ece7df;
  cursor: zoom-in;
  aspect-ratio: 16 / 10;
}

.photo-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.site-footer {
  padding: 68px clamp(24px, 7vw, 110px) 25px;
  background: linear-gradient(160deg, #0d3b36 0%, #0a2b28 100%);
  color: #fff;
}

.footer-main {
  display: grid;
  grid-template-columns: 1.5fr repeat(3, 1fr);
  gap: 50px;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.footer-logo img {
  width: 44px;
  height: 44px;
}

.footer-logo span {
  font-size: 22px;
  font-weight: 800;
}

.footer-brand p {
  max-width: 330px;
  margin: 22px 0 0;
  color: #a7c6c0;
  line-height: 1.55;
}

.footer-contact {
  display: inline-block;
  margin-top: 22px;
  padding: 12px 20px;
  background: #ff6f59;
  color: #fff;
  border-radius: 10px;
  font-weight: 750;
}

.footer-main h3 {
  margin: 0 0 16px;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.7;
}

.footer-main > div:not(.footer-brand) {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  margin-top: 48px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 13px;
  opacity: 0.65;
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: rgba(10, 8, 6, 0.88);
}

.lightbox img {
  max-width: min(1100px, 100%);
  max-height: 72vh;
  object-fit: contain;
  border-radius: 12px;
}

.lightbox-caption {
  max-width: 720px;
  margin-top: 18px;
  text-align: center;
  color: #fff;
}

.lightbox-caption p {
  margin: 8px 0 0;
  opacity: 0.8;
  line-height: 1.5;
}

.lightbox-close {
  position: absolute;
  top: 18px;
  right: 24px;
  background: none;
  border: 0;
  color: #fff;
  font-size: 36px;
  cursor: pointer;
}

@media (max-width: 900px) {
  .photo-grid,
  .footer-main {
    grid-template-columns: 1fr;
  }
}
</style>
